// BUILTIN_CLASS_EXTENSIONS_FOCUS_V2
// ASSIGNMENTS_SINGLE_SCROLL_WORKSPACE_V1
import { useState, useEffect, useCallback } from 'react';
import {
  fetchCoursePlacementLabels,
  getCourseGroupDisplayName,
  getStreamDisplayName
} from '../../../utils/groupLabels';
import {
  assignQiQuestionsForWeek
} from '../../../utils/qiAssignments';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders() {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };
}

async function fetchWeeks(courseId) {
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&cq_enabled=eq.true&select=*&order=week_number.asc`,
    { headers: apiHeaders() }
  );

  return r.json();
}

async function fetchEnrolments(courseId) {
  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=*,students(id,student_code,english_name,chinese_name,is_master)`,
    { headers: apiHeaders() }
  );

  const rows = await r.json();

  return (Array.isArray(rows) ? rows : [])
    .filter(
      enrolment =>
        enrolment?.students?.is_master !== true
    )
    .sort((a, b) =>
      String(a?.students?.student_code || '')
        .localeCompare(
          String(
            b?.students?.student_code ||
            ''
          ),
          undefined,
          {
            numeric: true,
            sensitivity: 'base'
          }
        )
    );
}

async function fetchCqSubmissions(weekIds) {
  if (!weekIds) return [];

  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?week_id=in.(${weekIds})&select=*`,
    { headers: apiHeaders() }
  );

  return r.json();
}

async function fetchQiAssignments(weekIds) {
  if (!weekIds) return [];

  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_question_assignments?course_week_id=in.(${weekIds})&select=*,cq_submissions(id,question_text,enrolment_id,question_type)`,
    { headers: apiHeaders() }
  );

  return r.json();
}

async function fetchQiSubmissions(assignmentIds) {
  if (!assignmentIds) return [];

  const r = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_question_improvements?cq_question_assignment_id=in.(${assignmentIds})&select=*`,
    { headers: apiHeaders() }
  );

  return r.json();
}


async function fetchQiExtensions(
  weekIds
) {
  if (!weekIds) {
    return [];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/qi_group_extensions?course_week_id=in.(${weekIds})&select=*`,
      {
        headers:
          apiHeaders()
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Loading QI extensions failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}


async function saveQiExtension({
  weekId,
  stream,
  groupNumber,
  extensionMinutes
}) {
  const response =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/qi_group_extensions' +
        '?on_conflict=course_week_id,stream,group_number',
      {
        method: 'POST',

        headers: {
          ...apiHeaders(),

          'Content-Type':
            'application/json',

          'Prefer':
            'resolution=merge-duplicates,return=representation'
        },

        body:
          JSON.stringify({
            course_week_id:
              weekId,

            stream:
              Number(stream),

            group_number:
              Number(
                groupNumber
              ),

            extension_minutes:
              Math.max(
                0,
                Number(
                  extensionMinutes
                ) || 0
              ),

            updated_at:
              new Date()
                .toISOString()
          })
      }
    );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Saving QI extension failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}


async function updateQiSubmission(
  submissionId,
  payload
) {
  const response = await fetch(
    SUPABASE_URL +
      '/rest/v1/cq_question_improvements?id=eq.' +
      encodeURIComponent(
        submissionId
      ),
    {
      method: 'PATCH',

      headers: {
        ...apiHeaders(),

        'Content-Type':
          'application/json',

        'Prefer':
          'return=representation'
      },

      body:
        JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Updating QI failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  return response.json();
}

async function createQiSubmission(
  payload
) {
  const response = await fetch(
    SUPABASE_URL +
      '/rest/v1/cq_question_improvements',
    {
      method: 'POST',

      headers: {
        ...apiHeaders(),

        'Content-Type':
          'application/json',

        'Prefer':
          'return=representation'
      },

      body:
        JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      'Creating QI draft failed (' +
        response.status +
        '): ' +
        detail
    );
  }

  const rows =
    await response.json();

  const saved =
    Array.isArray(rows)
      ? rows[0]
      : null;

  if (!saved) {
    throw new Error(
      'QI draft creation returned no record.'
    );
  }

  return saved;
}


function getStudentName(student) {
  const names = [
    student?.english_name,
    student?.chinese_name
  ].filter(Boolean);

  return (
    names.join(' / ') ||
    student?.student_code ||
    '—'
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';

  return new Date(
    dateStr
  ).toLocaleDateString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short'
    }
  );
}


function getQiExtensionMinutes(
  extensions,
  weekId,
  stream,
  groupNumber
) {
  const row =
    (extensions || []).find(
      extension =>
        String(
          extension
            .course_week_id
        ) ===
          String(weekId) &&
        Number(
          extension.stream
        ) ===
          Number(stream) &&
        Number(
          extension.group_number
        ) ===
          Number(
            groupNumber
          )
    );

  return Math.max(
    0,
    Number(
      row
        ?.extension_minutes
    ) || 0
  );
}


function getQiWindowStatus(
  week,
  course,
  extensionMinutes = 0
) {
  if (!week?.class_date) {
    return 'not_open';
  }

  const now =
    new Date();

  const classDate =
    new Date(
      `${week.class_date}T00:00:00`
    );

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const day =
    dayNames[
      classDate.getDay()
    ];

  const classTimeStr =
    (
      course?.class_times &&
      course.class_times[day]
    ) ||
    course?.class_time ||
    '09:00';

  const [
    hours,
    minutes
  ] =
    classTimeStr
      .split(':')
      .map(Number);

  const openTime =
    new Date(
      classDate
    );

  openTime.setHours(
    hours,
    minutes,
    0,
    0
  );

  const closeTime =
    new Date(
      openTime
    );

  closeTime.setHours(
    closeTime.getHours() +
      4
  );

  closeTime.setMinutes(
    closeTime.getMinutes() +
      Math.max(
        0,
        Number(
          extensionMinutes
        ) || 0
      )
  );

  if (now < openTime) {
    return 'not_open';
  }

  if (now > closeTime) {
    return 'closed';
  }

  return 'open';
}


// Status per student for a given week
function getStudentQiStatus(
  enrolment,
  week,
  weekAssignments,
  qiSubmissions,
  qiExtensions,
  course
) {
  if (!weekAssignments.length) {
    return 'not_assigned';
  }

  const assignmentIds =
    new Set(
      weekAssignments.map(
        assignment =>
          String(
            assignment.id
          )
      )
    );

  const targetId =
    String(
      enrolment.id
    );

  const participantWork =
    qiSubmissions.filter(
      submission => {
        if (
          !assignmentIds.has(
            String(
              submission
                .cq_question_assignment_id
            )
          )
        ) {
          return false;
        }

        if (
          String(
            submission
              .enrolment_id ||
            ''
          ) ===
          targetId
        ) {
          return true;
        }

        return (
          Array.isArray(
            submission
              .group_member_ids
          ) &&
          submission
            .group_member_ids
            .some(
              id =>
                String(id) ===
                targetId
            )
        );
      }
    );

  if (
    participantWork.some(
      submission =>
        submission.submitted ===
        true
    )
  ) {
    return 'submitted';
  }

  /*
   * If the student already has work recorded against
   * another QI group, that group's extension controls
   * their effective deadline.
   */
  const participationAssignment =
    participantWork.length > 0
      ? weekAssignments.find(
          assignment =>
            String(
              assignment.id
            ) ===
            String(
              participantWork[0]
                .cq_question_assignment_id
            )
        )
      : null;

  const deadlineStream =
    participationAssignment
      ?.stream ??
    enrolment.stream;

  const deadlineGroup =
    participationAssignment
      ?.group_number ??
    enrolment.group_number;

  const extensionMinutes =
    getQiExtensionMinutes(
      qiExtensions,
      week?.id,
      deadlineStream,
      deadlineGroup
    );

  const deadlinePassed =
    getQiWindowStatus(
      week,
      course,
      extensionMinutes
    ) ===
      'closed';

  /*
   * Once the effective deadline has passed, any
   * non-submitted work is simply Not Submitted.
   */
  if (deadlinePassed) {
    return 'not_submitted';
  }

  if (
    participantWork.length >
    0
  ) {
    return 'draft';
  }

  const assignedGroup =
    weekAssignments.filter(
      assignment =>
        Number(
          assignment.stream
        ) ===
          Number(
            enrolment.stream
          ) &&
        Number(
          assignment
            .group_number
        ) ===
          Number(
            enrolment
              .group_number
          )
    );

  if (!assignedGroup.length) {
    return 'not_assigned';
  }

  const assignedIds =
    new Set(
      assignedGroup.map(
        assignment =>
          String(
            assignment.id
          )
      )
    );

  /*
   * If the student's group has already submitted and
   * the student was omitted from the submission, that
   * is also simply Not Submitted.
   */
  const assignedGroupSubmitted =
    qiSubmissions.some(
      submission =>
        assignedIds.has(
          String(
            submission
              .cq_question_assignment_id
          )
        ) &&
        submission.submitted ===
          true
    );

  if (
    assignedGroupSubmitted
  ) {
    return 'not_submitted';
  }

  return 'assigned';
}


function getStudentCode(student) {
  return (
    student?.student_code ||
    '—'
  );
}


function getAssignedQuestionText(
  assignment,
  cqSubmissions
) {
  const directText =
    assignment
      ?.cq_submissions
      ?.question_text;

  if (
    typeof directText ===
      'string' &&
    directText.trim()
  ) {
    return directText;
  }

  const submission =
    cqSubmissions.find(
      item =>
        String(item.id) ===
        String(
          assignment
            ?.cq_submission_id
        )
    );

  return (
    submission?.question_text ||
    'Question text unavailable.'
  );
}


function findEnrolmentById(
  id,
  enrolments
) {
  return (
    enrolments.find(
      enrolment =>
        String(enrolment.id) ===
        String(id)
    ) ||
    null
  );
}


function uniqueIds(ids) {
  return [
    ...new Set(
      ids
        .filter(Boolean)
        .map(
          id =>
            String(id)
        )
    )
  ];
}


function StudentIdentity({
  enrolment
}) {
  if (!enrolment) {
    return (
      <span>
        Unknown student
      </span>
    );
  }

  return (
    <span>
      <strong>
        {getStudentCode(
          enrolment.students
        )}
      </strong>

      {' — '}

      {getStudentName(
        enrolment.students
      )}
    </span>
  );
}


function QiDetailModal({
  target,
  week,
  course,
  qiExtensions,
  enrolments,
  weekAssignments,
  qiSubmissions,
  cqSubmissions,
  coursePlacementLabels,
  onChanged,
  onClose
}) {
  const [
    participantCode,
    setParticipantCode
  ] = useState('');

  const [
    actionBusy,
    setActionBusy
  ] = useState(false);

  const [
    actionError,
    setActionError
  ] = useState('');

  const [
    actionNotice,
    setActionNotice
  ] = useState('');

  const [
    editingDraft,
    setEditingDraft
  ] = useState(false);

  const [
    editorSlots,
    setEditorSlots
  ] = useState([]);

  const [
    extensionHours,
    setExtensionHours
  ] = useState('0');

  const [
    extensionMinutesPart,
    setExtensionMinutesPart
  ] = useState('0');


  useEffect(() => {
    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        'Escape'
      ) {
        onClose();
      }
    }

    document.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [onClose]);


  const selectedEnrolment =
    enrolments.find(
      enrolment =>
        String(
          enrolment.id
        ) ===
        String(
          target.enrolmentId
        )
    );


  useEffect(() => {
    if (!selectedEnrolment) {
      setExtensionHours(
        '0'
      );

      setExtensionMinutesPart(
        '0'
      );

      return;
    }


    const assignmentIds =
      new Set(
        weekAssignments.map(
          assignment =>
            String(
              assignment.id
            )
        )
      );


    const targetId =
      String(
        selectedEnrolment.id
      );


    const participation =
      qiSubmissions.find(
        submission => {
          if (
            !assignmentIds.has(
              String(
                submission
                  .cq_question_assignment_id
              )
            )
          ) {
            return false;
          }

          if (
            String(
              submission.enrolment_id ||
              ''
            ) ===
              targetId
          ) {
            return true;
          }

          return (
            Array.isArray(
              submission
                .group_member_ids
            ) &&
            submission
              .group_member_ids
              .some(
                id =>
                  String(id) ===
                    targetId
              )
          );
        }
      );


    const participationAssignment =
      participation
        ? weekAssignments.find(
            assignment =>
              String(
                assignment.id
              ) ===
              String(
                participation
                  .cq_question_assignment_id
              )
          )
        : null;


    const stream =
      participationAssignment
        ?.stream ??
      selectedEnrolment.stream;


    const groupNumber =
      participationAssignment
        ?.group_number ??
      selectedEnrolment
        .group_number;


    const savedMinutes =
      getQiExtensionMinutes(
        qiExtensions,
        week?.id,
        stream,
        groupNumber
      );


    setExtensionHours(
      String(
        Math.floor(
          savedMinutes /
          60
        )
      )
    );


    setExtensionMinutesPart(
      String(
        savedMinutes %
        60
      )
    );

  }, [
    selectedEnrolment,
    week?.id,
    weekAssignments,
    qiSubmissions,
    qiExtensions
  ]);


  if (!selectedEnrolment) {
    return null;
  }


  const assignmentById =
    new Map(
      weekAssignments.map(
        assignment => [
          String(
            assignment.id
          ),
          assignment
        ]
      )
    );


  const weekAssignmentIds =
    new Set(
      weekAssignments.map(
        assignment =>
          String(
            assignment.id
          )
      )
    );


  const targetId =
    String(
      selectedEnrolment.id
    );


  const participationWork =
    qiSubmissions.filter(
      submission => {
        if (
          !weekAssignmentIds.has(
            String(
              submission
                .cq_question_assignment_id
            )
          )
        ) {
          return false;
        }

        if (
          String(
            submission.enrolment_id ||
            ''
          ) === targetId
        ) {
          return true;
        }

        return (
          Array.isArray(
            submission
              .group_member_ids
          ) &&
          submission
            .group_member_ids
            .some(
              id =>
                String(id) ===
                targetId
            )
        );
      }
    );


  const submittedParticipation =
    participationWork.filter(
      submission =>
        submission.submitted ===
        true
    );


  const currentStatus =
    getStudentQiStatus(
      selectedEnrolment,
      week,
      weekAssignments,
      qiSubmissions,
      qiExtensions,
      course
    );


  /*
   * A student who actually worked with another group should
   * open that group's work. A student marked Not in submission
   * stays on their nominal group, so the omission is visible.
   */
  const participationToShow =
    currentStatus ===
    'submitted'
      ? (
          submittedParticipation[0] ||
          null
        )
      : currentStatus ===
          'draft'
        ? (
            participationWork[0] ||
            null
          )
        : null;


  const participationAssignment =
    participationToShow
      ? assignmentById.get(
          String(
            participationToShow
              .cq_question_assignment_id
          )
        )
      : null;


  const displayStream =
    participationAssignment
      ?.stream ??
    selectedEnrolment.stream;


  const displayGroupNumber =
    participationAssignment
      ?.group_number ??
    selectedEnrolment
      .group_number;


  const workedWithDifferentGroup =
    participationAssignment &&
    (
      Number(
        displayStream
      ) !==
        Number(
          selectedEnrolment.stream
        ) ||
      Number(
        displayGroupNumber
      ) !==
        Number(
          selectedEnrolment
            .group_number
        )
    );


  const groupEnrolments =
    enrolments
      .filter(
        enrolment =>
          Number(
            enrolment.stream
          ) ===
            Number(
              displayStream
            ) &&
          Number(
            enrolment.group_number
          ) ===
            Number(
              displayGroupNumber
            )
      )
      .sort(
        (a, b) =>
          String(
            getStudentCode(
              a.students
            )
          ).localeCompare(
            String(
              getStudentCode(
                b.students
              )
            )
          )
      );


  const groupAssignments =
    weekAssignments.filter(
      assignment =>
        Number(
          assignment.stream
        ) ===
          Number(
            displayStream
          ) &&
        Number(
          assignment.group_number
        ) ===
          Number(
            displayGroupNumber
          )
    );


  const groupAssignmentIds =
    new Set(
      groupAssignments.map(
        assignment =>
          String(
            assignment.id
          )
      )
    );


  const groupWork =
    qiSubmissions.filter(
      submission =>
        groupAssignmentIds.has(
          String(
            submission
              .cq_question_assignment_id
          )
        )
    );


  const submittedWork =
    groupWork.filter(
      submission =>
        submission.submitted ===
        true
    );


  const finalWork =
    submittedWork.length > 0
      ? submittedWork
      : groupWork;


  const submitterIds =
    uniqueIds(
      submittedWork.map(
        submission =>
          submission.enrolment_id
      )
    );


  const participatingIds =
    uniqueIds([
      ...submittedWork.flatMap(
        submission =>
          Array.isArray(
            submission
              .group_member_ids
          )
            ? submission
                .group_member_ids
            : []
      ),

      ...submitterIds
    ]);


  const draftOwnerIds =
    uniqueIds(
      groupWork.map(
        submission =>
          submission.enrolment_id
      )
    );


  const recordedParticipantIds =
    uniqueIds([
      ...groupWork.map(
        submission =>
          submission.enrolment_id
      ),

      ...groupWork.flatMap(
        submission =>
          Array.isArray(
            submission
              .group_member_ids
          )
            ? submission
                .group_member_ids
            : []
      )
    ]);


  const visibleGroupEnrolments =
    [
      ...new Map(
        [
          ...groupEnrolments,

          ...enrolments.filter(
            enrolment =>
              recordedParticipantIds
                .includes(
                  String(
                    enrolment.id
                  )
                )
          )
        ].map(
          enrolment => [
            String(
              enrolment.id
            ),
            enrolment
          ]
        )
      ).values()
    ]
      .sort(
        (a, b) =>
          String(
            getStudentCode(
              a.students
            )
          ).localeCompare(
            String(
              getStudentCode(
                b.students
              )
            )
          )
      );


  function isNominalGroupMember(
    enrolment
  ) {
    return (
      Number(
        enrolment.stream
      ) ===
        Number(
          displayStream
        ) &&
      Number(
        enrolment
          .group_number
      ) ===
        Number(
          displayGroupNumber
        )
    );
  }


  async function refreshAfterAction() {
    if (
      typeof onChanged ===
      'function'
    ) {
      await onChanged();
    }
  }


  async function handleSaveQiExtension() {
    const hours =
      Number(
        extensionHours
      );

    const minutes =
      Number(
        extensionMinutesPart
      );

    if (
      !Number.isInteger(hours) ||
      hours < 0 ||
      !Number.isInteger(minutes) ||
      minutes < 0 ||
      minutes > 59
    ) {
      setActionError(
        'Extension must use whole hours and minutes (0–59 minutes).'
      );

      return;
    }

    const totalMinutes =
      (
        hours * 60
      ) +
      minutes;

    setActionBusy(true);
    setActionError('');
    setActionNotice('');

    try {
      await saveQiExtension({
        weekId:
          week.id,

        stream:
          displayStream,

        groupNumber:
          displayGroupNumber,

        extensionMinutes:
          totalMinutes
      });

      setActionNotice(
        totalMinutes > 0
          ? 'QI extension saved.'
          : 'QI extension removed.'
      );

      await refreshAfterAction();

    } catch (err) {
      console.error(
        'Saving QI extension failed:',
        err
      );

      setActionError(
        err?.message ||
        'Could not save the QI extension.'
      );

    } finally {
      setActionBusy(false);
    }
  }


  async function handleAddParticipant() {
    const code =
      participantCode.trim();

    setActionError('');
    setActionNotice('');

    if (!code) {
      return;
    }

    if (
      groupWork.length === 0
    ) {
      setActionError(
        'There is no saved QI work to attach this student to.'
      );

      return;
    }

    const match =
      enrolments.find(
        enrolment =>
          String(
            enrolment
              ?.students
              ?.student_code ||
            ''
          )
            .trim()
            .toLowerCase() ===
          code.toLowerCase()
      );

    if (!match) {
      setActionError(
        'No active student with that ID is enrolled in this course.'
      );

      return;
    }

    if (
      recordedParticipantIds
        .includes(
          String(
            match.id
          )
        )
    ) {
      setActionNotice(
        'That student is already recorded on this QI work.'
      );

      setParticipantCode('');

      return;
    }

    const nextParticipantIds =
      uniqueIds([
        ...recordedParticipantIds,
        match.id
      ]);

    setActionBusy(true);

    try {
      await Promise.all(
        groupWork.map(
          submission =>
            updateQiSubmission(
              submission.id,
              {
                group_member_ids:
                  nextParticipantIds
              }
            )
        )
      );

      setParticipantCode('');

      setActionNotice(
        'Student added.'
      );

      await refreshAfterAction();

    } catch (err) {
      console.error(
        'Adding QI participant failed:',
        err
      );

      setActionError(
        err?.message ||
        'Could not add the student.'
      );

    } finally {
      setActionBusy(false);
    }
  }


  function getWorkScore(
    submission
  ) {
    let score = 0;

    if (
      String(
        submission
          ?.revised_question ||
        ''
      ).trim()
    ) {
      score += 4;
    }

    if (
      String(
        submission
          ?.problems_identified ||
        ''
      ).trim()
    ) {
      score += 2;
    }

    if (
      String(
        submission
          ?.fix_explanation ||
        ''
      ).trim()
    ) {
      score += 1;
    }

    return score;
  }


  async function handleSubmitDraft() {
    if (
      groupWork.length ===
      0
    ) {
      return;
    }

    setActionBusy(true);
    setActionError('');
    setActionNotice('');

    const now =
      new Date()
        .toISOString();

    try {
      const rowsToPatch = [];

      groupAssignments.forEach(
        assignment => {
          const rows =
            groupWork.filter(
              submission =>
                String(
                  submission
                    .cq_question_assignment_id
                ) ===
                String(
                  assignment.id
                )
            );

          if (
            rows.length ===
            0
          ) {
            return;
          }

          const existingSubmitted =
            rows.filter(
              submission =>
                submission.submitted ===
                true
            );

          if (
            existingSubmitted.length >
            0
          ) {
            rowsToPatch.push(
              ...existingSubmitted
            );

            return;
          }

          const bestDraft =
            rows
              .slice()
              .sort(
                (a, b) =>
                  getWorkScore(b) -
                  getWorkScore(a)
              )[0];

          if (bestDraft) {
            rowsToPatch.push(
              bestDraft
            );
          }
        }
      );

      const uniqueRows =
        [
          ...new Map(
            rowsToPatch.map(
              row => [
                String(
                  row.id
                ),
                row
              ]
            )
          ).values()
        ];

      if (
        uniqueRows.length ===
        0
      ) {
        throw new Error(
          'There is no saved draft work to submit.'
        );
      }

      await Promise.all(
        uniqueRows.map(
          submission =>
            updateQiSubmission(
              submission.id,
              {
                group_member_ids:
                  recordedParticipantIds,

                submitted:
                  true,

                submitted_at:
                  submission
                    .submitted_at ||
                  now
              }
            )
        )
      );

      setActionNotice(
        'Saved draft work submitted.'
      );

      await refreshAfterAction();

    } catch (err) {
      console.error(
        'Submitting QI draft failed:',
        err
      );

      setActionError(
        err?.message ||
        'Could not submit the draft.'
      );

    } finally {
      setActionBusy(false);
    }
  }


  const hasUnsubmittedWork =
    groupWork.some(
      submission =>
        submission.submitted !==
        true
    );


  const requiredQuestionCount =
    groupAssignments.length >= 2
      ? 2
      : groupAssignments.length;


  const canEditSubmission =
    submittedWork.length === 0 &&
    groupAssignments.length > 0;


  function getBestDraftForAssignment(
    assignmentId
  ) {
    return (
      groupWork
        .filter(
          submission =>
            submission.submitted !==
              true &&
            String(
              submission
                .cq_question_assignment_id
            ) ===
              String(
                assignmentId
              )
        )
        .slice()
        .sort(
          (a, b) =>
            getWorkScore(b) -
            getWorkScore(a)
        )[0] ||
      null
    );
  }


  function makeEditorSlot(
    submission = null
  ) {
    return {
      sourceSubmissionId:
        submission?.id ||
        null,

      assignmentId:
        submission
          ?.cq_question_assignment_id ||
        '',

      problems_identified:
        submission
          ?.problems_identified ||
        '',

      revised_question:
        submission
          ?.revised_question ||
        '',

      fix_explanation:
        submission
          ?.fix_explanation ||
        ''
    };
  }


  function handleStartDraftEditing() {
    const distinctDrafts =
      [];

    const seenAssignments =
      new Set();

    groupWork
      .filter(
        submission =>
          submission.submitted !==
          true
      )
      .slice()
      .sort(
        (a, b) =>
          getWorkScore(b) -
          getWorkScore(a)
      )
      .forEach(
        submission => {
          const assignmentId =
            String(
              submission
                .cq_question_assignment_id ||
              ''
            );

          if (
            !assignmentId ||
            seenAssignments.has(
              assignmentId
            )
          ) {
            return;
          }

          seenAssignments.add(
            assignmentId
          );

          distinctDrafts.push(
            submission
          );
        }
      );

    const slots =
      Array.from(
        {
          length:
            requiredQuestionCount
        },
        (
          _,
          index
        ) =>
          makeEditorSlot(
            distinctDrafts[
              index
            ] ||
            null
          )
      );

    setEditorSlots(
      slots
    );

    setActionError('');
    setActionNotice('');
    setEditingDraft(true);
  }


  function handleEditorQuestionChange(
    slotIndex,
    assignmentId
  ) {
    setEditorSlots(
      previous =>
        previous.map(
          (
            slot,
            index
          ) => {
            if (
              index !==
              slotIndex
            ) {
              return slot;
            }

            if (!assignmentId) {
              return {
                ...slot,

                assignmentId:
                  '',

                problems_identified:
                  '',

                revised_question:
                  '',

                fix_explanation:
                  ''
              };
            }

            const existing =
              getBestDraftForAssignment(
                assignmentId
              );

            if (existing) {
              return makeEditorSlot(
                existing
              );
            }

            /*
             * If this slot already corresponds to an unsubmitted
             * row, keep that row ID. Saving can safely move that
             * draft to the newly selected question rather than
             * leaving an unnecessary stale draft behind.
             */
            return {
              sourceSubmissionId:
                slot
                  .sourceSubmissionId,

              assignmentId,

              problems_identified:
                '',

              revised_question:
                '',

              fix_explanation:
                ''
            };
          }
        )
    );

    setActionError('');
    setActionNotice('');
  }


  function handleDraftFieldChange(
    slotIndex,
    field,
    value
  ) {
    setEditorSlots(
      previous =>
        previous.map(
          (
            slot,
            index
          ) =>
            index ===
            slotIndex
              ? {
                  ...slot,

                  [field]:
                    value
                }
              : slot
        )
    );
  }


  function handleCancelDraftEditing() {
    setEditingDraft(false);
    setEditorSlots([]);
    setActionError('');
  }


  async function handleSaveDraftEdits() {
    const selectedSlots =
      editorSlots.filter(
        slot =>
          !!slot.assignmentId
      );

    if (
      selectedSlots.length ===
      0
    ) {
      setActionError(
        'Choose at least one question before saving.'
      );

      return;
    }


    const selectedIds =
      selectedSlots.map(
        slot =>
          String(
            slot.assignmentId
          )
      );


    if (
      new Set(
        selectedIds
      ).size !==
      selectedIds.length
    ) {
      setActionError(
        'Question 1 and Question 2 must be different questions.'
      );

      return;
    }


    const templateSubmission =
      groupWork.find(
        submission =>
          submission.submitted !==
          true
      ) ||
      groupWork[0] ||
      null;


    const ownerEnrolmentId =
      templateSubmission
        ?.enrolment_id ||
      selectedEnrolment.id;


    const memberIds =
      Array.isArray(
        templateSubmission
          ?.group_member_ids
      )
        ? templateSubmission
            .group_member_ids
        : [];


    setActionBusy(true);
    setActionError('');
    setActionNotice('');


    try {
      for (
        const slot of
          selectedSlots
      ) {
        const payload = {
          cq_question_assignment_id:
            slot.assignmentId,

          enrolment_id:
            ownerEnrolmentId,

          group_member_ids:
            memberIds,

          problems_identified:
            slot
              .problems_identified ||
            '',

          revised_question:
            slot
              .revised_question ||
            '',

          fix_explanation:
            slot
              .fix_explanation ||
            '',

          submitted:
            false,

          submitted_at:
            null
        };


        if (
          slot.sourceSubmissionId
        ) {
          await updateQiSubmission(
            slot.sourceSubmissionId,
            payload
          );

        } else {
          await createQiSubmission(
            payload
          );
        }
      }


      setEditingDraft(false);
      setEditorSlots([]);

      setActionNotice(
        'Draft changes saved.'
      );

      await refreshAfterAction();

    } catch (err) {
      console.error(
        'Editing QI draft failed:',
        err
      );

      setActionError(
        err?.message ||
        'Could not save the draft changes.'
      );

    } finally {
      setActionBusy(false);
    }
  }


  return (
    <div
      style={
        detailStyles.overlay
      }
      onMouseDown={
        onClose
      }
      role="presentation"
    >
      <div
        style={
          detailStyles.modal
        }
        onMouseDown={
          event =>
            event.stopPropagation()
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="qi-detail-title"
      >
        <div
          style={
            detailStyles.header
          }
        >
          <div>
            <h3
              id="qi-detail-title"
              style={
                detailStyles.title
              }
            >
              Question Improvement — {getStreamDisplayName(
                coursePlacementLabels,
                displayStream
              )}, {getCourseGroupDisplayName(
                coursePlacementLabels,
                displayStream,
                displayGroupNumber
              )}
            </h3>

            <div
              style={
                detailStyles.subtitle
              }
            >
              Week {week?.week_number ?? '—'} · {STATUS_STYLES[currentStatus]?.label || currentStatus}

              {workedWithDifferentGroup && (
                <>
                  {' · '}
                  Assigned group: {getCourseGroupDisplayName(
                    coursePlacementLabels,
                    selectedEnrolment.stream,
                    selectedEnrolment.group_number
                  )}
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            style={
              detailStyles.closeBtn
            }
            onClick={
              onClose
            }
            aria-label="Close"
          >
            ×
          </button>
        </div>


        <div
          style={
            detailStyles.body
          }
        >
          <section
            style={
              detailStyles.section
            }
          >
            <h4
              style={
                detailStyles.sectionTitle
              }
            >
              Students in this group
            </h4>

            <div
              style={
                detailStyles.tableWrap
              }
            >
              <table
                style={
                  detailStyles.table
                }
              >
                <thead>
                  <tr>
                    <th
                      style={
                        detailStyles.th
                      }
                    >
                      ID
                    </th>

                    <th
                      style={
                        detailStyles.th
                      }
                    >
                      Name
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {visibleGroupEnrolments.map(
                    enrolment => (
                      <tr
                        key={
                          enrolment.id
                        }
                      >
                        <td
                          style={
                            detailStyles.td
                          }
                        >
                          {getStudentCode(
                            enrolment.students
                          )}
                        </td>

                        <td
                          style={
                            detailStyles.td
                          }
                        >
                          {getStudentName(
                            enrolment.students
                          )}

                          {!isNominalGroupMember(
                            enrolment
                          ) && (
                            <span
                              style={
                                detailStyles.addedParticipantBadge
                              }
                            >
                              Added
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </section>


          <section
            style={
              detailStyles.section
            }
          >
            <h4
              style={
                detailStyles.sectionTitle
              }
            >
              Questions assigned
            </h4>

            {groupAssignments.length >
            0 ? (
              <ol
                style={
                  detailStyles.questionList
                }
              >
                {groupAssignments.map(
                  assignment => (
                    <li
                      key={
                        assignment.id
                      }
                      style={
                        detailStyles.questionItem
                      }
                    >
                      {getAssignedQuestionText(
                        assignment,
                        cqSubmissions
                      )}
                    </li>
                  )
                )}
              </ol>
            ) : (
              <div
                style={
                  detailStyles.muted
                }
              >
                No questions are assigned to this group.
              </div>
            )}
          </section>


          {groupAssignments.length > 0 && (
            <section
              style={
                detailStyles.section
              }
            >
              <h4
                style={
                  detailStyles.sectionTitle
                }
              >
                Instructor controls
              </h4>

              <div
                style={
                  detailStyles.extensionBox
                }
              >
                <div
                  style={
                    detailStyles.extensionTitle
                  }
                >
                  QI extension
                </div>

                <div
                  style={
                    detailStyles.extensionHint
                  }
                >
                  Extends this group's normal four-hour QI window.
                </div>

                <div
                  style={
                    detailStyles.extensionControls
                  }
                >
                  <label
                    style={
                      detailStyles.extensionLabel
                    }
                  >
                    Hours

                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={
                        extensionHours
                      }
                      style={
                        detailStyles.extensionInput
                      }
                      onChange={
                        event =>
                          setExtensionHours(
                            event.target.value
                          )
                      }
                      disabled={
                        actionBusy
                      }
                    />
                  </label>

                  <label
                    style={
                      detailStyles.extensionLabel
                    }
                  >
                    Minutes

                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={
                        extensionMinutesPart
                      }
                      style={
                        detailStyles.extensionInput
                      }
                      onChange={
                        event =>
                          setExtensionMinutesPart(
                            event.target.value
                          )
                      }
                      disabled={
                        actionBusy
                      }
                    />
                  </label>

                  <button
                    type="button"
                    style={
                      detailStyles.secondaryAction
                    }
                    onClick={
                      handleSaveQiExtension
                    }
                    disabled={
                      actionBusy
                    }
                  >
                    Save extension
                  </button>
                </div>
              </div>

              <div
                style={
                  detailStyles.actionRow
                }
              >
                <input
                  type="text"
                  value={
                    participantCode
                  }
                  style={
                    detailStyles.participantInput
                  }
                  placeholder="Student ID"
                  onChange={
                    event => {
                      setParticipantCode(
                        event.target.value
                      );

                      setActionError(
                        ''
                      );

                      setActionNotice(
                        ''
                      );
                    }
                  }
                  disabled={
                    actionBusy
                  }
                />

                <button
                  type="button"
                  style={
                    detailStyles.secondaryAction
                  }
                  onClick={
                    handleAddParticipant
                  }
                  disabled={
                    actionBusy ||
                    !participantCode.trim()
                  }
                >
                  Add student
                </button>

                {canEditSubmission && (
                  <button
                    type="button"
                    style={
                      detailStyles.secondaryAction
                    }
                    onClick={
                      handleStartDraftEditing
                    }
                    disabled={
                      actionBusy ||
                      editingDraft
                    }
                  >
                    Edit Submission
                  </button>
                )}

                {hasUnsubmittedWork && (
                  <button
                    type="button"
                    style={
                      detailStyles.primaryAction
                    }
                    onClick={
                      handleSubmitDraft
                    }
                    disabled={
                      actionBusy
                    }
                  >
                    Submit draft
                  </button>
                )}
              </div>

              {actionError && (
                <div
                  style={
                    detailStyles.actionError
                  }
                >
                  {actionError}
                </div>
              )}

              {actionNotice && (
                <div
                  style={
                    detailStyles.actionNotice
                  }
                >
                  {actionNotice}
                </div>
              )}

              {editingDraft && (
                <div
                  style={
                    detailStyles.draftEditor
                  }
                >
                  <div
                    style={
                      detailStyles.draftEditorHeader
                    }
                  >
                    <div>
                      <div
                        style={
                          detailStyles.draftEditorTitle
                        }
                      >
                        Edit Submission
                      </div>

                      <div
                        style={
                          detailStyles.draftEditorHint
                        }
                      >
                        Choose the questions and edit their draft answers. Missing draft answers will be created automatically. Submission status and participants are not changed.
                      </div>
                    </div>
                  </div>

                  <div
                    style={
                      detailStyles.draftEditorList
                    }
                  >
                    {editorSlots.map(
                      (
                        slot,
                        index
                      ) => {
                        const otherSelectedIds =
                          new Set(
                            editorSlots
                              .filter(
                                (
                                  _,
                                  otherIndex
                                ) =>
                                  otherIndex !==
                                  index
                              )
                              .map(
                                other =>
                                  String(
                                    other
                                      .assignmentId ||
                                    ''
                                  )
                              )
                              .filter(Boolean)
                          );

                        const assignment =
                          groupAssignments.find(
                            item =>
                              String(
                                item.id
                              ) ===
                              String(
                                slot.assignmentId
                              )
                          ) ||
                          null;

                        return (
                          <div
                            key={
                              'editor-slot-' +
                              index
                            }
                            style={
                              detailStyles.draftEditorCard
                            }
                          >
                            <div
                              style={
                                detailStyles.responseHeading
                              }
                            >
                              Question {index + 1}
                            </div>

                            <div
                              style={
                                detailStyles.editorField
                              }
                            >
                              <label
                                style={
                                  detailStyles.answerLabel
                                }
                              >
                                Question chosen
                              </label>

                              <select
                                style={
                                  detailStyles.editorSelect
                                }
                                value={
                                  slot.assignmentId
                                }
                                disabled={
                                  actionBusy
                                }
                                onChange={
                                  event =>
                                    handleEditorQuestionChange(
                                      index,
                                      event.target.value
                                    )
                                }
                              >
                                <option value="">
                                  Select a question...
                                </option>

                                {groupAssignments.map(
                                  candidate => {
                                    const candidateId =
                                      String(
                                        candidate.id
                                      );

                                    if (
                                      otherSelectedIds.has(
                                        candidateId
                                      )
                                    ) {
                                      return null;
                                    }

                                    return (
                                      <option
                                        key={
                                          candidate.id
                                        }
                                        value={
                                          candidate.id
                                        }
                                      >
                                        {getAssignedQuestionText(
                                          candidate,
                                          cqSubmissions
                                        )}
                                      </option>
                                    );
                                  }
                                )}
                              </select>
                            </div>

                            {assignment && (
                              <>
                                <div
                                  style={
                                    detailStyles.editorField
                                  }
                                >
                                  <div
                                    style={
                                      detailStyles.answerLabel
                                    }
                                  >
                                    Original question
                                  </div>

                                  <div
                                    style={
                                      detailStyles.editorQuestion
                                    }
                                  >
                                    {getAssignedQuestionText(
                                      assignment,
                                      cqSubmissions
                                    )}
                                  </div>
                                </div>

                                <div
                                  style={
                                    detailStyles.editorField
                                  }
                                >
                                  <label
                                    style={
                                      detailStyles.answerLabel
                                    }
                                  >
                                    Problems identified
                                  </label>

                                  <textarea
                                    style={
                                      detailStyles.editorTextarea
                                    }
                                    value={
                                      slot
                                        .problems_identified
                                    }
                                    onChange={
                                      event =>
                                        handleDraftFieldChange(
                                          index,
                                          'problems_identified',
                                          event.target.value
                                        )
                                    }
                                    disabled={
                                      actionBusy
                                    }
                                  />
                                </div>

                                <div
                                  style={
                                    detailStyles.editorField
                                  }
                                >
                                  <label
                                    style={
                                      detailStyles.answerLabel
                                    }
                                  >
                                    Revised Question
                                  </label>

                                  <textarea
                                    style={
                                      detailStyles.editorTextarea
                                    }
                                    value={
                                      slot
                                        .revised_question
                                    }
                                    onChange={
                                      event =>
                                        handleDraftFieldChange(
                                          index,
                                          'revised_question',
                                          event.target.value
                                        )
                                    }
                                    disabled={
                                      actionBusy
                                    }
                                  />
                                </div>

                                <div
                                  style={
                                    detailStyles.editorField
                                  }
                                >
                                  <label
                                    style={
                                      detailStyles.answerLabel
                                    }
                                  >
                                    How it fixes the problem
                                  </label>

                                  <textarea
                                    style={
                                      detailStyles.editorTextarea
                                    }
                                    value={
                                      slot
                                        .fix_explanation
                                    }
                                    onChange={
                                      event =>
                                        handleDraftFieldChange(
                                          index,
                                          'fix_explanation',
                                          event.target.value
                                        )
                                    }
                                    disabled={
                                      actionBusy
                                    }
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>


                  <div
                    style={
                      detailStyles.draftEditorActions
                    }
                  >
                    <button
                      type="button"
                      style={
                        detailStyles.secondaryAction
                      }
                      onClick={
                        handleCancelDraftEditing
                      }
                      disabled={
                        actionBusy
                      }
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      style={
                        detailStyles.primaryAction
                      }
                      onClick={
                        handleSaveDraftEdits
                      }
                      disabled={
                        actionBusy
                      }
                    >
                      {actionBusy
                        ? 'Saving...'
                        : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}


          {submittedWork.length >
            0 && (
            <section
              style={
                detailStyles.section
              }
            >
              <h4
                style={
                  detailStyles.sectionTitle
                }
              >
                Submission participants
              </h4>

              <div
                style={
                  detailStyles.identityBlock
                }
              >
                <div
                  style={
                    detailStyles.identityLabel
                  }
                >
                  Submitted by
                </div>

                {submitterIds.map(
                  id => (
                    <div
                      key={
                        `submitter-${id}`
                      }
                      style={
                        detailStyles.identityRow
                      }
                    >
                      <StudentIdentity
                        enrolment={
                          findEnrolmentById(
                            id,
                            enrolments
                          )
                        }
                      />
                    </div>
                  )
                )}
              </div>

              <div
                style={
                  detailStyles.identityBlock
                }
              >
                <div
                  style={
                    detailStyles.identityLabel
                  }
                >
                  Students recorded as participating
                </div>

                {participatingIds.map(
                  id => (
                    <div
                      key={
                        `participant-${id}`
                      }
                      style={
                        detailStyles.identityRow
                      }
                    >
                      <StudentIdentity
                        enrolment={
                          findEnrolmentById(
                            id,
                            enrolments
                          )
                        }
                      />
                    </div>
                  )
                )}
              </div>
            </section>
          )}


          {submittedWork.length ===
            0 &&
            groupWork.length >
              0 && (
            <section
              style={
                detailStyles.section
              }
            >
              <h4
                style={
                  detailStyles.sectionTitle
                }
              >
                Draft work
              </h4>

              <div
                style={
                  detailStyles.muted
                }
              >
                No final submission has been recorded yet.
              </div>

              {draftOwnerIds.length >
                0 && (
                <div
                  style={
                    detailStyles.identityBlock
                  }
                >
                  <div
                    style={
                      detailStyles.identityLabel
                    }
                  >
                    Draft saved by
                  </div>

                  {draftOwnerIds.map(
                    id => (
                      <div
                        key={
                          `draft-owner-${id}`
                        }
                        style={
                          detailStyles.identityRow
                        }
                      >
                        <StudentIdentity
                          enrolment={
                            findEnrolmentById(
                              id,
                              enrolments
                            )
                          }
                        />
                      </div>
                    )
                  )}
                </div>
              )}
            </section>
          )}


          {finalWork.length > 0 && (
            <section
              style={
                detailStyles.section
              }
            >
              <h4
                style={
                  detailStyles.sectionTitle
                }
              >
                {submittedWork.length >
                0
                  ? 'Submitted answers'
                  : 'Saved draft answers'}
              </h4>

              <div
                style={
                  detailStyles.responseList
                }
              >
                {finalWork.map(
                  (
                    submission,
                    index
                  ) => {
                    const assignment =
                      groupAssignments.find(
                        item =>
                          String(
                            item.id
                          ) ===
                          String(
                            submission
                              .cq_question_assignment_id
                          )
                      );

                    return (
                      <div
                        key={
                          submission.id
                        }
                        style={
                          detailStyles.responseCard
                        }
                      >
                        <div
                          style={
                            detailStyles.responseHeading
                          }
                        >
                          Question {index + 1}
                        </div>


                        <div
                          style={
                            detailStyles.answerBlock
                          }
                        >
                          <div
                            style={
                              detailStyles.answerLabel
                            }
                          >
                            Question chosen
                          </div>

                          <div
                            style={
                              detailStyles.answerText
                            }
                          >
                            {assignment
                              ? getAssignedQuestionText(
                                  assignment,
                                  cqSubmissions
                                )
                              : 'Question text unavailable.'}
                          </div>
                        </div>


                        <div
                          style={
                            detailStyles.answerBlock
                          }
                        >
                          <div
                            style={
                              detailStyles.answerLabel
                            }
                          >
                            Problems identified
                          </div>

                          <div
                            style={
                              detailStyles.answerText
                            }
                          >
                            {submission.problems_identified ||
                              '—'}
                          </div>
                        </div>


                        <div
                          style={
                            detailStyles.answerBlock
                          }
                        >
                          <div
                            style={
                              detailStyles.answerLabel
                            }
                          >
                            Revised Question
                          </div>

                          <div
                            style={
                              detailStyles.revisedQuestionRow
                            }
                          >
                            <div
                              style={
                                detailStyles.revisedQuestionText
                              }
                            >
                              {submission.revised_question ||
                                '—'}
                            </div>
                          </div>
                        </div>


                        <div
                          style={
                            detailStyles.answerBlock
                          }
                        >
                          <div
                            style={
                              detailStyles.answerLabel
                            }
                          >
                            How it fixes the problem
                          </div>

                          <div
                            style={
                              detailStyles.answerText
                            }
                          >
                            {submission.fix_explanation ||
                              '—'}
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </section>
          )}


          {groupWork.length === 0 && (
            <div
              style={
                detailStyles.notice
              }
            >
              No Question Improvement work has been saved by this group yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function QiAssignmentsModal({
  week,
  weekAssignments,
  cqSubmissions,
  coursePlacementLabels,
  onClose
}) {
  useEffect(() => {
    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        'Escape'
      ) {
        onClose();
      }
    }

    document.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [onClose]);


  const groupMap =
    new Map();


  (
    weekAssignments ||
    []
  ).forEach(
    assignment => {
      const stream =
        Number(
          assignment.stream
        );

      const groupNumber =
        Number(
          assignment.group_number
        );

      const key =
        String(stream) +
        ':' +
        String(groupNumber);

      if (
        !groupMap.has(key)
      ) {
        groupMap.set(
          key,
          {
            stream,
            groupNumber,
            assignments: []
          }
        );
      }

      groupMap
        .get(key)
        .assignments
        .push(
          assignment
        );
    }
  );


  const groups =
    [
      ...groupMap.values()
    ]
      .sort(
        (a, b) =>
          a.stream -
            b.stream ||
          a.groupNumber -
            b.groupNumber
      );


  const streams =
    [
      ...new Set(
        groups.map(
          group =>
            group.stream
        )
      )
    ];


  return (
    <div
      style={
        detailStyles.overlay
      }
      onMouseDown={
        onClose
      }
      role="presentation"
    >
      <div
        style={
          detailStyles.modal
        }
        onMouseDown={
          event =>
            event.stopPropagation()
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="qi-assignments-title"
      >
        <div
          style={
            detailStyles.header
          }
        >
          <div>
            <h3
              id="qi-assignments-title"
              style={
                detailStyles.title
              }
            >
              QI Assignments — Week {week?.week_number ?? '—'}
            </h3>

            <div
              style={
                detailStyles.subtitle
              }
            >
              {weekAssignments.length} question assignment
              {weekAssignments.length ===
              1
                ? ''
                : 's'}
            </div>
          </div>

          <button
            type="button"
            style={
              detailStyles.closeBtn
            }
            onClick={
              onClose
            }
            aria-label="Close"
          >
            ×
          </button>
        </div>


        <div
          style={
            detailStyles.body
          }
        >
          {streams.map(
            stream => {
              const streamGroups =
                groups.filter(
                  group =>
                    group.stream ===
                    stream
                );

              return (
                <section
                  key={
                    stream
                  }
                  style={
                    detailStyles.allocationStream
                  }
                >
                  <h4
                    style={
                      detailStyles.allocationStreamTitle
                    }
                  >
                    {getStreamDisplayName(
                      coursePlacementLabels,
                      stream
                    )}
                  </h4>

                  <div
                    style={
                      detailStyles.allocationGrid
                    }
                  >
                    {streamGroups.map(
                      group => (
                        <div
                          key={
                            String(
                              group.stream
                            ) +
                            ':' +
                            String(
                              group.groupNumber
                            )
                          }
                          style={
                            detailStyles.allocationCard
                          }
                        >
                          <div
                            style={
                              detailStyles.allocationGroupHeader
                            }
                          >
                            <strong>
                              {getCourseGroupDisplayName(
                                coursePlacementLabels,
                                group.stream,
                                group.groupNumber
                              )}
                            </strong>

                            <span
                              style={
                                detailStyles.allocationCount
                              }
                            >
                              {group.assignments.length} question
                              {group.assignments.length ===
                              1
                                ? ''
                                : 's'}
                            </span>
                          </div>

                          <ol
                            style={
                              detailStyles.questionList
                            }
                          >
                            {group.assignments.map(
                              assignment => (
                                <li
                                  key={
                                    assignment.id
                                  }
                                  style={
                                    detailStyles.questionItem
                                  }
                                >
                                  {getAssignedQuestionText(
                                    assignment,
                                    cqSubmissions
                                  )}
                                </li>
                              )
                            )}
                          </ol>
                        </div>
                      )
                    )}
                  </div>
                </section>
              );
            }
          )}

          {groups.length === 0 && (
            <div
              style={
                detailStyles.notice
              }
            >
              No QI questions have been assigned for this week.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const detailStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    background:
      'rgba(31, 41, 51, 0.48)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '12px',
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehavior: 'contain',
    WebkitOverflowScrolling: 'touch'
  },

  modal: {
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto',
    background: 'white',
    borderRadius: '12px',
    boxShadow:
      '0 18px 55px rgba(0, 0, 0, 0.24)',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column'
  },

  header: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent:
      'space-between',
    gap: '16px',
    padding:
      '16px 18px',
    borderBottom:
      '1px solid #d7dee5',
    background: 'white',
    borderRadius: '12px 12px 0 0',
    flexShrink: 0
  },

  title: {
    margin: 0,
    fontSize: '18px',
    color: '#1f2933'
  },

  subtitle: {
    marginTop: '4px',
    color: '#66788a',
    fontSize: '13px'
  },

  closeBtn: {
    width: '34px',
    height: '34px',
    border: 'none',
    borderRadius: '8px',
    background: '#f0f4f7',
    color: '#1f2933',
    fontSize: '24px',
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0
  },

  body: {
    padding: '18px',
    overflow: 'visible'
  },

  section: {
    marginBottom: '22px'
  },

  sectionTitle: {
    margin:
      '0 0 10px',
    fontSize: '15px',
    color: '#1f2933'
  },

  tableWrap: {
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    overflow: 'hidden'
  },

  table: {
    width: '100%',
    borderCollapse:
      'collapse'
  },

  th: {
    textAlign: 'left',
    padding: '8px 10px',
    background: '#f4f7f9',
    color: '#42515f',
    fontSize: '12px',
    borderBottom:
      '1px solid #d7dee5'
  },

  td: {
    padding: '8px 10px',
    color: '#1f2933',
    fontSize: '13px',
    borderBottom:
      '1px solid #e3e9ee'
  },

  questionList: {
    margin: 0,
    paddingLeft: '24px'
  },

  questionItem: {
    marginBottom: '8px',
    color: '#1f2933',
    fontSize: '14px',
    lineHeight: '1.5'
  },

  identityBlock: {
    marginBottom: '12px'
  },

  identityLabel: {
    color: '#66788a',
    fontSize: '12px',
    fontWeight: '700',
    marginBottom: '4px',
    textTransform:
      'uppercase',
    letterSpacing:
      '0.03em'
  },

  identityRow: {
    color: '#1f2933',
    fontSize: '14px',
    marginBottom: '3px'
  },

  responseList: {
    display: 'grid',
    gap: '12px'
  },

  responseCard: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px',
    background: '#fff'
  },

  responseHeading: {
    fontWeight: '700',
    fontSize: '14px',
    color: '#215b7d',
    marginBottom: '12px'
  },

  answerBlock: {
    marginBottom: '12px'
  },

  answerLabel: {
    fontWeight: '700',
    fontSize: '12px',
    color: '#42515f',
    marginBottom: '4px'
  },

  answerText: {
    fontSize: '14px',
    color: '#1f2933',
    lineHeight: '1.5',
    whiteSpace:
      'pre-wrap'
  },

  revisedQuestionRow: {
    display: 'flex',
    alignItems:
      'flex-start',
    gap: '7px'
  },

  revisedQuestionText: {
    flex: 1,
    minWidth: 0,
    fontSize: '14px',
    color: '#1f2933',
    lineHeight: '1.5',
    whiteSpace:
      'pre-wrap',
    overflowWrap:
      'anywhere'
  },

  muted: {
    color: '#66788a',
    fontSize: '13px'
  },

  allocationStream: {
    marginBottom: '26px'
  },

  allocationStreamTitle: {
    margin:
      '0 0 10px',
    color: '#215b7d',
    fontSize: '16px',
    fontWeight: '800'
  },

  allocationGrid: {
    display: 'grid',
    gap: '12px'
  },

  allocationCard: {
    border:
      '1px solid #d7dee5',
    borderRadius: '9px',
    background: '#fff',
    padding:
      '12px 14px'
  },

  allocationGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
    gap: '12px',
    marginBottom: '10px',
    color: '#1f2933',
    fontSize: '14px'
  },

  allocationCount: {
    flexShrink: 0,
    background: '#f0f4f7',
    color: '#66788a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  actionRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    alignItems: 'center'
  },

  extensionBox: {
    border:
      '1px solid #d7dee5',
    borderRadius:
      '9px',
    background:
      '#f7f9fa',
    padding:
      '12px',
    marginBottom:
      '14px'
  },

  extensionTitle: {
    fontSize:
      '13px',
    fontWeight:
      '800',
    color:
      '#1f2933',
    marginBottom:
      '3px'
  },

  extensionHint: {
    fontSize:
      '11px',
    color:
      '#66788a',
    marginBottom:
      '9px'
  },

  extensionControls: {
    display:
      'flex',
    gap:
      '10px',
    alignItems:
      'flex-end',
    flexWrap:
      'wrap'
  },

  extensionLabel: {
    display:
      'flex',
    flexDirection:
      'column',
    gap:
      '4px',
    fontSize:
      '11px',
    fontWeight:
      '700',
    color:
      '#66788a'
  },

  extensionInput: {
    width:
      '85px',
    border:
      '1px solid #b8c7d3',
    borderRadius:
      '7px',
    padding:
      '7px 9px',
    fontSize:
      '13px',
    background:
      'white'
  },

  addedParticipantBadge: {
    display:
      'inline-block',
    marginLeft:
      '8px',
    padding:
      '2px 7px',
    borderRadius:
      '999px',
    background:
      '#e7f1f7',
    color:
      '#215b7d',
    fontSize:
      '10px',
    fontWeight:
      '800',
    verticalAlign:
      'middle'
  },

  participantInput: {
    minWidth: '180px',
    border:
      '1px solid #c8d2dc',
    borderRadius: '7px',
    padding: '8px 10px',
    fontSize: '13px'
  },

  secondaryAction: {
    border:
      '1px solid #9fb3c8',
    background: 'white',
    color: '#215b7d',
    borderRadius: '7px',
    padding:
      '8px 11px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  primaryAction: {
    border: 'none',
    background: '#215b7d',
    color: 'white',
    borderRadius: '7px',
    padding:
      '8px 11px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  actionError: {
    marginTop: '8px',
    color: '#a12a2a',
    fontSize: '12px'
  },

  actionNotice: {
    marginTop: '8px',
    color: '#216e45',
    fontSize: '12px',
    fontWeight: '700'
  },

  draftEditor: {
    marginTop: '16px',
    border:
      '1px solid #c8d2dc',
    borderRadius: '10px',
    padding: '14px',
    background: '#f9fbfc'
  },

  draftEditorHeader: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems:
      'flex-start',
    gap: '12px',
    marginBottom: '14px'
  },

  draftEditorTitle: {
    color: '#1f2933',
    fontSize: '15px',
    fontWeight: '800',
    marginBottom: '3px'
  },

  draftEditorHint: {
    color: '#66788a',
    fontSize: '12px',
    lineHeight: '1.45'
  },

  draftEditorList: {
    display: 'grid',
    gap: '12px'
  },

  draftEditorCard: {
    border:
      '1px solid #d7dee5',
    borderRadius: '9px',
    padding: '13px',
    background: 'white'
  },

  editorField: {
    marginBottom: '13px'
  },

  editorQuestion: {
    color: '#1f2933',
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace:
      'pre-wrap',
    padding:
      '8px 10px',
    background: '#f4f7f9',
    borderRadius: '7px',
    border:
      '1px solid #e0e7ed'
  },

  editorSelect: {
    display: 'block',
    width: '100%',
    border:
      '1px solid #b8c7d3',
    borderRadius: '7px',
    padding:
      '9px 10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    color: '#1f2933',
    background: 'white',
    boxSizing:
      'border-box'
  },

  editorTextarea: {
    width: '100%',
    minHeight: '90px',
    resize: 'vertical',
    border:
      '1px solid #b8c7d3',
    borderRadius: '7px',
    padding:
      '9px 10px',
    fontSize: '14px',
    fontFamily: 'inherit',
    lineHeight: '1.45',
    color: '#1f2933',
    background: 'white',
    boxSizing:
      'border-box'
  },

  draftEditorActions: {
    display: 'flex',
    justifyContent:
      'flex-end',
    alignItems: 'center',
    gap: '8px',
    marginTop: '14px'
  },

  notice: {
    background: '#f7f9fa',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding:
      '12px 14px',
    color: '#66788a',
    fontSize: '13px'
  }
};


const STATUS_STYLES = {
  not_assigned: {
    background: '#f0f4f7',
    color: '#66788a',
    label: '—'
  },

  assigned: {
    background: '#fff4d7',
    color: '#8a5b00',
    label: 'Assigned'
  },

  draft: {
    background: '#e7f1f7',
    color: '#215b7d',
    label: 'Draft'
  },

  submitted: {
    background: '#e7f5ed',
    color: '#216e45',
    label: 'Submitted'
  },

  not_submitted: {
    background: '#faeaea',
    color: '#a12a2a',
    label:
      'Not Submitted'
  },

  not_complete: {
    background: '#faeaea',
    color: '#a12a2a',
    label: 'Not Complete'
  }
};


export default function QuestionImprovement({
  course
}) {
  const [
    weeks,
    setWeeks
  ] = useState([]);

  const [
    enrolments,
    setEnrolments
  ] = useState([]);

  const [
    cqSubmissions,
    setCqSubmissions
  ] = useState([]);

  const [
    qiAssignments,
    setQiAssignments
  ] = useState([]);

  const [
    qiSubmissions,
    setQiSubmissions
  ] = useState([]);

  const [
    qiExtensions,
    setQiExtensions
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    assigning,
    setAssigning
  ] = useState({});

  const [
    selectedWeekId,
    setSelectedWeekId
  ] = useState(null);

  const [
    detailTarget,
    setDetailTarget
  ] = useState(null);

  const [
    assignmentOverviewOpen,
    setAssignmentOverviewOpen
  ] = useState(false);

  const [
    coursePlacementLabels,
    setCoursePlacementLabels
  ] = useState({
    streams: {},
    groups: {}
  });

  const [
    error,
    setError
  ] = useState('');

  const [
    classExtension,
    setClassExtension
  ] =
    useState(null);


  useEffect(() => {
    if (
      (!detailTarget &&
        !assignmentOverviewOpen &&
        !classExtension) ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      'hidden';

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [
    detailTarget,
    assignmentOverviewOpen,
    classExtension
  ]);


  const load =
    useCallback(
      async () => {
        const [
          w,
          e
        ] =
          await Promise.all([
            fetchWeeks(
              course.id
            ),

            fetchEnrolments(
              course.id
            )
          ]);

        setWeeks(w);
        setEnrolments(e);

        if (
          w.length > 0
        ) {
          const weekIds =
            w
              .map(
                wk =>
                  wk.id
              )
              .join(',');

          const [
            cq,
            qa,
            extensions
          ] =
            await Promise.all([
              fetchCqSubmissions(
                weekIds
              ),

              fetchQiAssignments(
                weekIds
              ),

              fetchQiExtensions(
                weekIds
              )
            ]);

          setCqSubmissions(
            cq
          );

          setQiAssignments(
            qa
          );

          setQiExtensions(
            Array.isArray(
              extensions
            )
              ? extensions
              : []
          );

          if (
            qa.length > 0
          ) {
            const assignmentIds =
              qa
                .map(
                  a =>
                    a.id
                )
                .join(',');

            const qs =
              await fetchQiSubmissions(
                assignmentIds
              );

            setQiSubmissions(
              qs
            );

          } else {
            setQiSubmissions(
              []
            );
          }

          const today =
            new Date()
              .toISOString()
              .split(
                'T'
              )[0];

          const pastWeeks =
            w.filter(
              wk =>
                wk.class_date <=
                today
            );

          const defaultWeekId =
            pastWeeks.length > 0
              ? pastWeeks[
                  pastWeeks.length -
                  1
                ].id
              : w[0].id;

          setSelectedWeekId(
            previous =>
              previous &&
              w.some(
                wk =>
                  wk.id ===
                  previous
              )
                ? previous
                : defaultWeekId
          );

        } else {
          setCqSubmissions(
            []
          );

          setQiAssignments(
            []
          );

          setQiSubmissions(
            []
          );

          setQiExtensions(
            []
          );

          setSelectedWeekId(
            null
          );
        }

        setLoading(
          false
        );
      },
      [
        course.id
      ]
    );


  useEffect(
    () => {
      load();
    },
    [
      load
    ]
  );


  useEffect(() => {
    let cancelled =
      false;

    fetchCoursePlacementLabels(
      course.id
    )
      .then(
        labels => {
          if (
            !cancelled
          ) {
            setCoursePlacementLabels(
              labels || {
                streams: {},
                groups: {}
              }
            );
          }
        }
      )
      .catch(
        err => {
          if (
            !cancelled
          ) {
            console.error(
              'Loading Stream / Group names failed:',
              err
            );
          }
        }
      );

    return () => {
      cancelled =
        true;
    };
  }, [course.id]);


  async function handleAssign(
    weekId
  ) {
    setAssigning(
      previous => ({
        ...previous,

        [weekId]:
          true
      })
    );

    setError('');

    try {
      await assignQiQuestionsForWeek({
        weekId,
        cqSubmissions,
        enrolments,
        allowReassign:
          true
      });

      await load();

    } catch (err) {
      console.error(
        'QI assignment failed:',
        err
      );

      setError(
        err?.message ||
        'QI assignment failed.'
      );

    } finally {
      setAssigning(
        previous => ({
          ...previous,

          [weekId]:
            false
        })
      );
    }
  }


  const selectedWeek =
    weeks.find(
      w =>
        w.id ===
        selectedWeekId
    );


  const weekCqSubs =
    cqSubmissions.filter(
      submission =>
        submission.week_id ===
          selectedWeekId &&
        submission.question_type ===
          'Critical'
    );


  const weekAssignments =
    qiAssignments.filter(
      a =>
        a.course_week_id ===
        selectedWeekId
    );


  function classExtensionGroups(){
    const m=
      new Map();

    weekAssignments.forEach(
      a=>{
        const s=
          Number(a.stream);

        const g=
          Number(
            a.group_number
          );

        if(
          !Number.isFinite(s) ||
          !Number.isFinite(g)
        ){
          return;
        }

        const k=
          s+':'+g;

        if(!m.has(k)){
          m.set(
            k,
            {
              stream:s,
              groupNumber:g
            }
          );
        }
      }
    );

    return [
      ...m.values()
    ];
  }


  function openClassExtension(){
    const groups=
      classExtensionGroups();

    if(
      !groups.length ||
      !selectedWeek
    ){
      setError(
        'Question Improvement must be assigned before a class extension can be set.'
      );

      return;
    }


    const vals=[
      ...new Set(
        groups.map(
          x=>
            getQiExtensionMinutes(
              qiExtensions,
              selectedWeek.id,
              x.stream,
              x.groupNumber
            )
        )
      )
    ];


    const total=
      vals.length===1
        ?vals[0]
        :null;


    setClassExtension({
      hours:
        total==null
          ?''
          :String(
              Math.floor(
                total/60
              )
            ),

      minutes:
        total==null
          ?''
          :String(
              total%60
            ),

      mixed:
        vals.length>1,

      saving:false,
      error:'',
      notice:''
    });
  }


  async function saveClassExtension(){
    if(
      !classExtension ||
      classExtension.saving ||
      !selectedWeek
    ){
      return;
    }


    const h=
      Number(
        classExtension.hours
      );

    const m=
      Number(
        classExtension.minutes
      );


    if(
      !Number.isInteger(h) ||
      h<0 ||
      !Number.isInteger(m) ||
      m<0 ||
      m>59
    ){
      setClassExtension(
        v=>({
          ...v,

          error:
            'Extension must use whole hours and minutes (0–59 minutes).'
        })
      );

      return;
    }


    const total=
      h*60+m;

    const groups=
      classExtensionGroups();


    setClassExtension(
      v=>({
        ...v,
        saving:true,
        error:'',
        notice:''
      })
    );


    const rs=
      await Promise.allSettled(
        groups.map(
          x=>
            saveQiExtension({
              weekId:
                selectedWeek.id,

              stream:
                x.stream,

              groupNumber:
                x.groupNumber,

              extensionMinutes:
                total
            })
        )
      );


    const failed=
      rs.filter(
        r=>
          r.status===
          'rejected'
      );


    try{
      await load();

    }catch(err){
      console.error(
        'QI extension refresh failed:',
        err
      );
    }


    if(failed.length){
      setClassExtension(
        v=>({
          ...v,
          saving:false,

          error:
            'Saved for '+
            (
              groups.length-
              failed.length
            )+
            ' of '+
            groups.length+
            ' QI groups. '+
            failed.length+
            ' failed.'
        })
      );

      return;
    }


    setClassExtension(
      v=>({
        ...v,

        hours:
          String(h),

        minutes:
          String(m),

        mixed:false,
        saving:false,

        notice:
          total>0
            ?'QI class extension saved.'
            :'QI class extension removed.'
      })
    );
  }




  const isAssigned =
    weekAssignments.length >
    0;


  const weekAssignmentIds =
    new Set(
      weekAssignments.map(
        a =>
          a.id
      )
    );


  const hasQiWork =
    qiSubmissions.some(
      submission =>
        weekAssignmentIds.has(
          submission
            .cq_question_assignment_id
        )
    );


  const canAssignOrReassign =
    weekCqSubs.length >
      0 &&
    !assigning[
      selectedWeekId
    ] &&
    (
      !isAssigned ||
      !hasQiWork
    );


  const today =
    new Date()
      .toISOString()
      .split(
        'T'
      )[0];


  const isCqClosed =
    selectedWeek
      ? (
          selectedWeek
            .class_date <=
          today
        )
      : false;


  const isPending =
    isCqClosed &&
    !isAssigned &&
    weekCqSubs.length >
      0;


  if (loading) {
    return (
      <div
        style={
          styles.muted
        }
      >
        Loading...
      </div>
    );
  }


  if (
    weeks.length ===
    0
  ) {
    return (
      <div
        style={
          styles.muted
        }
      >
        No weeks have CQ enabled. Enable CQ per week in the Teaching Schedule.
      </div>
    );
  }


  return (
    <div>
      {assignmentOverviewOpen && (
        <QiAssignmentsModal
          week={
            selectedWeek
          }
          weekAssignments={
            weekAssignments
          }
          cqSubmissions={
            cqSubmissions
          }
          coursePlacementLabels={
            coursePlacementLabels
          }
          onClose={() =>
            setAssignmentOverviewOpen(
              false
            )
          }
        />
      )}


      {detailTarget && (
        <QiDetailModal
          target={
            detailTarget
          }
          week={
            selectedWeek
          }
          course={
            course
          }
          qiExtensions={
            qiExtensions
          }
          enrolments={
            enrolments
          }
          weekAssignments={
            weekAssignments
          }
          qiSubmissions={
            qiSubmissions
          }
          cqSubmissions={
            cqSubmissions
          }
          coursePlacementLabels={
            coursePlacementLabels
          }
          onChanged={
            load
          }
          onClose={() =>
            setDetailTarget(
              null
            )
          }
        />
      )}


      {classExtension&&(
        <div
          style={
            detailStyles.overlay
          }
          onMouseDown={()=>
            !classExtension.saving &&
            setClassExtension(
              null
            )
          }
          role="presentation"
        >
          <div
            style={{
              ...detailStyles.modal,
              maxWidth:'440px'
            }}
            onMouseDown={
              e=>
                e.stopPropagation()
            }
            role="dialog"
            aria-modal="true"
            aria-label="Question Improvement class extension"
          >
            <div
              style={
                detailStyles.header
              }
            >
              <div>
                <h3
                  style={
                    detailStyles.title
                  }
                >
                  Class Extension
                </h3>

                <div
                  style={
                    detailStyles
                      .subtitle
                  }
                >
                  Question Improvement
                  {' — '}
                  Week {
                    selectedWeek
                      ?.week_number ??
                    '—'
                  }
                </div>
              </div>

              <button
                type="button"
                style={
                  detailStyles
                    .closeBtn
                }
                disabled={
                  classExtension
                    .saving
                }
                onClick={()=>
                  setClassExtension(
                    null
                  )
                }
              >
                ×
              </button>
            </div>

            <div
              style={
                detailStyles.body
              }
            >
              <div
                style={
                  detailStyles
                    .extensionBox
                }
              >
                <div
                  style={
                    detailStyles
                      .extensionTitle
                  }
                >
                  QI extension
                </div>

                <div
                  style={
                    detailStyles
                      .extensionHint
                  }
                >
                  Applies the existing
                  QI extension to every
                  assigned group for
                  this week.
                </div>

                {classExtension
                  .mixed&&(
                  <div
                    style={
                      detailStyles
                        .extensionHint
                    }
                  >
                    Current group
                    extensions vary.
                    Saving here will
                    replace them with
                    the class value
                    below.
                  </div>
                )}

                <div
                  style={
                    detailStyles
                      .extensionControls
                  }
                >
                  <label
                    style={
                      detailStyles
                        .extensionLabel
                    }
                  >
                    Hours

                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={
                        classExtension
                          .hours
                      }
                      style={
                        detailStyles
                          .extensionInput
                      }
                      disabled={
                        classExtension
                          .saving
                      }
                      onChange={
                        e=>
                          setClassExtension(
                            v=>({
                              ...v,
                              hours:
                                e.target
                                  .value
                            })
                          )
                      }
                      autoFocus
                    />
                  </label>

                  <label
                    style={
                      detailStyles
                        .extensionLabel
                    }
                  >
                    Minutes

                    <input
                      type="number"
                      min="0"
                      max="59"
                      step="1"
                      value={
                        classExtension
                          .minutes
                      }
                      style={
                        detailStyles
                          .extensionInput
                      }
                      disabled={
                        classExtension
                          .saving
                      }
                      onChange={
                        e=>
                          setClassExtension(
                            v=>({
                              ...v,
                              minutes:
                                e.target
                                  .value
                            })
                          )
                      }
                    />
                  </label>

                  <button
                    type="button"
                    style={
                      detailStyles
                        .secondaryAction
                    }
                    disabled={
                      classExtension
                        .saving
                    }
                    onClick={
                      saveClassExtension
                    }
                  >
                    {
                      classExtension
                        .saving
                        ?'Saving...'
                        :'Save extension'
                    }
                  </button>
                </div>

                {classExtension
                  .error&&(
                  <div
                    style={
                      detailStyles
                        .actionError
                    }
                  >
                    {
                      classExtension
                        .error
                    }
                  </div>
                )}

                {classExtension
                  .notice&&(
                  <div
                    style={
                      detailStyles
                        .actionNotice
                    }
                  >
                    {
                      classExtension
                        .notice
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}




      {error && (
        <div
          style={
            styles.error
          }
        >
          {error}
        </div>
      )}


      <div
        style={
          styles.weekSelector
        }
      >
        {weeks.map(
          w => (
            <button
              key={
                w.id
              }
              style={{
                ...styles.weekBtn,

                ...(
                  w.id ===
                  selectedWeekId
                    ? styles.weekBtnActive
                    : {}
                )
              }}
              onClick={() =>
                setSelectedWeekId(
                  w.id
                )
              }
            >
              W{w.week_number}

              <span
                style={
                  styles.weekDate
                }
              >
                {formatDate(
                  w.class_date
                )}
              </span>
            </button>
          )
        )}
      </div>


      {selectedWeek && (
        <div>
          <div
            style={
              styles.statusBar
            }
          >
            <div
              style={
                styles.statusInfo
              }
            >
              <span
                style={
                  styles
                    .classExtensionHeading
                }
              >
                <strong>
                  Week {
                    selectedWeek
                      .week_number
                  }
                </strong>

                {isAssigned&&(
                  <button
                    type="button"
                    style={
                      styles
                        .classExtensionPlus
                    }
                    onClick={
                      openClassExtension
                    }
                    title="Class extension"
                    aria-label={
                      'Class extension for Question Improvement Week '+
                      selectedWeek
                        .week_number
                    }
                  >
                    +
                  </button>
                )}
              </span>

              {' · '}

              {weekCqSubs.length} Critical Question
              {weekCqSubs.length !==
              1
                ? 's'
                : ''} submitted


              {isPending && (
                <span
                  style={
                    styles.pendingBadge
                  }
                >
                  Assignment pending
                </span>
              )}


              {isAssigned && (
                <>
                  <span
                    style={
                      styles.assignedBadge
                    }
                  >
                    Assigned — {weekAssignments.length} question
                    {weekAssignments.length !==
                    1
                      ? 's'
                      : ''} distributed
                  </span>

                  <button
                    type="button"
                    style={
                      styles.viewAssignmentsBtn
                    }
                    onClick={() =>
                      setAssignmentOverviewOpen(
                        true
                      )
                    }
                  >
                    View Assignments
                  </button>
                </>
              )}
            </div>


            <button
              style={{
                ...styles.assignBtn,

                opacity:
                  canAssignOrReassign
                    ? 1
                    : 0.5,

                cursor:
                  canAssignOrReassign
                    ? 'pointer'
                    : 'default'
              }}
              disabled={
                !canAssignOrReassign
              }
              onClick={() =>
                handleAssign(
                  selectedWeekId
                )
              }
              title={
                isAssigned &&
                hasQiWork
                  ? 'Students have already started Question Improvement work, so these questions can no longer be reassigned.'
                  : ''
              }
            >
              {assigning[
                selectedWeekId
              ]
                ? 'Assigning...'
                : isAssigned
                  ? hasQiWork
                    ? '✓ Assigned'
                    : 'Reassign Questions'
                  : 'Assign Questions'}
            </button>
          </div>


          <div
            style={
              styles.tableWrap
            }
          >
            <table
              style={
                styles.table
              }
            >
              <thead>
                <tr>
                  <th
                    style={
                      styles.thName
                    }
                  >
                    Student
                  </th>

                  <th
                    style={
                      styles.th
                    }
                  >
                    Stream
                  </th>

                  <th
                    style={
                      styles.th
                    }
                  >
                    Group
                  </th>

                  <th
                    style={
                      styles.th
                    }
                  >
                    QI Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {enrolments.map(
                  enrolment => {
                    const status =
                      getStudentQiStatus(
                        enrolment,
                        selectedWeek,
                        weekAssignments,
                        qiSubmissions,
                        qiExtensions,
                        course
                      );

                    const st =
                      STATUS_STYLES[
                        status
                      ] ||
                      STATUS_STYLES
                        .not_assigned;

                    const canOpen =
                      status !==
                      'not_assigned';

                    return (
                      <tr
                        key={
                          enrolment.id
                        }
                      >
                        <td
                          style={
                            styles.tdName
                          }
                        >
                          {getStudentCode(
                            enrolment.students
                          )}

                          {' — '}

                          {getStudentName(
                            enrolment.students
                          )}
                        </td>

                        <td
                          style={
                            styles.td
                          }
                        >
                          {enrolment.stream ===
                            null ||
                          enrolment.stream ===
                            undefined
                            ? '—'
                            : getStreamDisplayName(
                                coursePlacementLabels,
                                enrolment.stream
                              )}
                        </td>

                        <td
                          style={
                            styles.td
                          }
                        >
                          {enrolment.group_number ===
                            null ||
                          enrolment.group_number ===
                            undefined
                            ? '—'
                            : getCourseGroupDisplayName(
                                coursePlacementLabels,
                                enrolment.stream,
                                enrolment.group_number
                              )}
                        </td>

                        <td
                          style={
                            styles.td
                          }
                        >
                          <button
                            style={{
                              ...styles.statusCell,

                              background:
                                st.background,

                              color:
                                st.color,

                              cursor:
                                canOpen
                                  ? 'pointer'
                                  : 'default'
                            }}
                            disabled={
                              !canOpen
                            }
                            onMouseDown={
                              canOpen
                                ?e=>
                                  e.preventDefault()
                                :undefined
                            }
                            onClick={
                              canOpen
                                ? () =>
                                    setDetailTarget({
                                      enrolmentId:
                                        enrolment.id,

                                      weekId:
                                        selectedWeekId,

                                      status
                                    })
                                : undefined
                            }
                          >
                            {st.label}
                          </button>
                        </td>
                      </tr>
                    );
                  }
                )}


                {enrolments.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan={
                        4
                      }
                      style={
                        styles.emptyCell
                      }
                    >
                      No active students.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


const styles = {
  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin:
      '8px 0'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    padding:
      '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  weekSelector: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '16px'
  },

  weekBtn: {
    border:
      '1px solid #d7dee5',
    background: 'white',
    borderRadius: '8px',
    padding:
      '6px 10px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    color: '#66788a',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px'
  },

  weekBtnActive: {
    background: '#215b7d',
    borderColor: '#215b7d',
    color: 'white'
  },

  weekDate: {
    fontSize: '10px',
    fontWeight: '400'
  },

  statusBar: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    padding:
      '12px 16px',
    background: '#f7f9fa',
    borderRadius: '10px',
    border:
      '1px solid #d7dee5',
    marginBottom: '16px',
    gap: '12px',
    flexWrap: 'wrap'
  },

  classExtensionHeading:{
    display:'inline-flex',
    alignItems:'center',
    gap:'5px'
  },

  classExtensionPlus:{
    width:'20px',
    height:'20px',
    display:'inline-flex',
    alignItems:'center',
    justifyContent:'center',
    border:'1px solid #b8c5ce',
    borderRadius:'50%',
    background:'white',
    color:'#215b7d',
    fontSize:'15px',
    fontWeight:'700',
    lineHeight:1,
    padding:0,
    cursor:'pointer'
  },

  statusInfo: {
    fontSize: '14px',
    color: '#1f2933',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },

  pendingBadge: {
    background: '#fff4d7',
    color: '#8a5b00',
    borderRadius: '999px',
    padding:
      '2px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  assignedBadge: {
    background: '#e7f5ed',
    color: '#216e45',
    borderRadius: '999px',
    padding:
      '2px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  viewAssignmentsBtn: {
    border:
      '1px solid #b8c7d3',
    background: 'white',
    color: '#215b7d',
    borderRadius: '7px',
    padding:
      '5px 10px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  assignBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding:
      '8px 16px',
    fontSize: '13px',
    fontWeight: '600'
  },

  tableWrap: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    overflow: 'hidden'
  },

  table: {
    width: '100%',
    borderCollapse:
      'collapse'
  },

  thName: {
    textAlign: 'left',
    padding:
      '9px 11px',
    fontSize: '13px',
    color: '#42515f',
    background: '#f0f4f7',
    borderBottom:
      '1px solid #d7dee5',
    width: '200px'
  },

  th: {
    textAlign: 'left',
    padding:
      '9px 11px',
    fontSize: '13px',
    color: '#42515f',
    background: '#f0f4f7',
    borderBottom:
      '1px solid #d7dee5'
  },

  tdName: {
    padding:
      '9px 11px',
    borderBottom:
      '1px solid #d7dee5',
    fontSize: '14px',
    fontWeight: '600'
  },

  td: {
    padding:
      '9px 11px',
    borderBottom:
      '1px solid #d7dee5',
    fontSize: '14px'
  },

  emptyCell: {
    padding: '18px',
    textAlign: 'center',
    color: '#66788a',
    fontSize: '13px'
  },

  statusCell: {
    border: 'none',
    borderRadius: '6px',
    padding:
      '4px 10px',
    fontSize: '12px',
    fontWeight: '700'
  }
};